import {
  useState,
  useCallback,
  SetStateAction,
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactNode,
  ReactPortal,
  useEffect
} from 'react';
import {
  Drawer,
  TextField,
  Stack,
  Typography,
  Divider,
  Box,
  Grid,
  Tab,
  Tabs,
  Button,
  FormControl,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useForm, FormProvider, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import DialogToolBar from './AdditionData/DialogToolBar';
import {CustomFile, UploadProps} from 'src/minimal-components/upload';
import {
  RHFCheckbox,
  RHFSelect,
  RHFSingleUpload
} from 'src/minimal-components/hook-form';
import Resources from './AdditionData/Resources';
import NewFilesLockProperties from './AdditionData/NewFilesLockProperties';
import {fileDatas} from 'src/minimal-components/file-thumbnail';
import EncryptUtils from 'src/utils/EncryptUtils';
import uuidv4 from 'src/utils/uuidv4';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';
import {LoadingButton} from '@mui/lab';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {BASE_RECORD_ID} from 'src/routes/BaseRoute';

const StyledLabel = styled('span')(({theme}) => ({
  ...theme.typography.caption,
  fontSize: '0.9rem',
  flexShrink: 0,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightSemiBold
}));
const SubmissionTypes = [
  {value: 'NoSubmission', label: 'No Submission'},
  {value: 'Select', label: 'Select'},
  {value: 'FileAttachment', label: 'File Attachment'},
  {value: 'TextSubmission', label: 'Text Submission'}
];
// Validation schema using yup
const validationSchema = yup.object().shape({
  taskName: yup.string().required('Assignment name is required'),
  MaximumMarks: yup.number().required('Maximum marks are required'),
  Duration: yup.number().required('Duration is required'),
  description: yup.string().notRequired(),
  MarkMandatory: yup.boolean().nullable().notRequired()
});

interface IKanbanTask {
  id: string;
  name: string;
  MaximumMarks: number;
  Duration: number;
  description?: string;
  MarkMandatory?: boolean;
}
interface Propsnew extends Omit<UploadProps, 'file'> {
  multiple?: boolean;
}

interface Props extends Propsnew {
  task: IKanbanTask;
  uiFieldsdata: any;
  // eslint-disable-next-line react/boolean-prop-naming
  openDetails: boolean;
  taskDone: any;
  onCloseDetails: VoidFunction;
  onUpdateTask?: (updateTask: IKanbanTask) => void;
}

export default function AddOfflineTest({
  task,
  uiFieldsdata,
  openDetails,
  onCloseDetails,
  onUpdateTask,
  taskDone,
  ...other
}: Props) {
  const {
    appState: {applicationName}
  } = useAppContext();
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const [tabActive, setTabsActive] = useState<boolean>(true);
  const [saved, setSaved] = useState<boolean>(false);
  const [uiFields, setUiFields] = useState<any>(uiFieldsdata);
  const [insertedId, setInsertedId] = useState<any>();
  // console.log('assignme', uiFields, insertedId);
  const [isloaded, setIsLoaded] = useState(false);
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      taskName: task?.name || '',
      MaximumMarks: task?.MaximumMarks || undefined,
      Duration: task?.Duration || undefined,
      description: task?.description || '',
      MarkMandatory: task?.MarkMandatory || false
    }
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [offlineloader, setOfflineLoader] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const handleChange = (event: any, newValue: SetStateAction<number>) => {
    if (tabActive) {
      setOpenSnackbar(true); // Show the snackbar when `tabActive` is true
    } else {
      setActiveTab(newValue); // Switch tabs
    }
  };
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
    setSaved(false);
  };
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {errors, isSubmitting}
  } = methods;

  const values = watch();
  // console.log('values', values, errors);

  // Handle removing all files
  const init = async () => {
    try {
      setOfflineLoader(true);
      let res;
      if (uiFieldsdata === undefined) {
        res = await APIService.getSearchForm(
          applicationName,
          'module',
          'AddOfflineTest'
        );
        await setUiFields(res.searchForm);
      } else {
        setUiFields(uiFieldsdata);
      }

      if ((res?.searchForm || uiFieldsdata !== undefined) && insertedId) {
        // console.log('insertedId', insertedId, uiFields);
        const {sql_data} =
          uiFieldsdata['create_sql'] || res.searchForm['create_sql'];
        let operations;
        if (insertedId) {
          operations = 'SEARCH';
        }
        const data: any = {
          sql_data: sql_data,
          data: {id: insertedId.data[0].id, course_overview_id: uniqueId},
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getLMSResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        // console.log('results', result);

        setOfflineLoader(false);
      }

      setOfflineLoader(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setOfflineLoader(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      await init();
      if (taskDone?.operation === 'INSERT') {
        setInsertedId(undefined);
      }
    };

    fetchData();
  }, []);
  const onSubmit = async (datavalue: any) => {
    onUpdateTask?.({
      ...task,
      name: datavalue.taskName,
      MaximumMarks: datavalue.MaximumMarks,
      description: datavalue?.description,
      MarkMandatory: datavalue?.MarkMandatory
    });
    try {
      if (uiFields) {
        const {sql_data} = uiFields['create_sql'];
        let operations;
        if (taskDone?.id && taskDone?.operation) {
          operations = taskDone?.operation;
          datavalue.module_id = taskDone?.id;
        }
        if (insertedId && insertedId.data.length > 0) {
          operations = 'UPDATE';
          datavalue.id = insertedId.data[0].inserted_id;
          datavalue.course_overview_id = insertedId.data[0].course_overview_id;
        }
        datavalue.course_overview_id = uniqueId;
        // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        setIsLoaded(true);
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getLMSResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        setInsertedId(result);
        setIsLoaded(false);
        setSaved(true);
        setTabsActive(false);
        setActiveTab(1);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
    setTabsActive(false);
  };

  return (
    <FormProvider {...methods}>
      <Drawer
        open={openDetails}
        onClose={onCloseDetails}
        anchor="right"
        slotProps={{
          backdrop: {invisible: true}
        }}
        PaperProps={{
          sx: {
            width: {
              xs: 1,
              sm: '47vw'
            }
          }
        }}>
        <DialogToolBar
          title="Add Offline Test"
          onCloseDetails={onCloseDetails}
        />
        <Divider />
        <Box sx={{width: '100%'}}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}>
            <Grid item xs={12}>
              <Tabs
                value={activeTab}
                onChange={handleChange}
                variant="fullWidth"
                textColor="primary"
                sx={{
                  backgroundColor: '#F4F6F8',
                  '& .MuiTab-root': {
                    fontSize: '16px',
                    fontWeight: '500',
                    '&:hover': {
                      backgroundColor: '#e0e0e0', // Change background on hover
                      borderRadius: 2
                    }
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: 'primary.main',
                    margin: 1,
                    backgroundColor: 'white',
                    borderRadius: 2
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#637381',
                    height: 0
                  }
                }}>
                <Tab label="Basic Info" />
                <Tab
                  label="Lock Properties"
                  disabled={offlineloader || isloaded}
                />
                <Tab label="Resources" disabled={offlineloader || isloaded} />
              </Tabs>
            </Grid>
          </Grid>
        </Box>
        {activeTab === 0 &&
          (offlineloader ? (
            <Stack
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              minHeight={'50vh'}>
              <LoadingScreen />
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} style={{overflowY: 'auto'}}>
              <Stack spacing={1.5} sx={{pt: 3, pb: 5, px: 2.5}}>
                {/* Task Name */}
                <Stack spacing={2} direction="column">
                  <StyledLabel>Name</StyledLabel>
                  <Controller
                    name="taskName"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        error={!!errors.taskName}
                        helperText={errors.taskName?.message}
                      />
                    )}
                  />
                </Stack>
                <Stack spacing={2} direction="column">
                  <StyledLabel>Maximum Marks</StyledLabel>
                  <Controller
                    name="MaximumMarks"
                    control={control}
                    rules={{
                      validate: value => value !== 0 || 'Marks cannot be 0' // Custom validation
                    }}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="number"
                        error={!!errors.MaximumMarks}
                        helperText={errors.MaximumMarks?.message}
                        onChange={event => {
                          const newValue = event.target.value;
                          if (newValue === '' || parseInt(newValue, 10) > 0) {
                            field.onChange(
                              newValue === '' ? null : parseInt(newValue, 10)
                            );
                          }
                        }}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                </Stack>
                <Stack spacing={2} direction="column">
                  <StyledLabel>Duration(Minutes)</StyledLabel>
                  <Controller
                    name="Duration"
                    control={control}
                    rules={{
                      validate: value => value !== 0 || 'Duration cannot be 0' // Custom validation
                    }}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="number"
                        error={!!errors.Duration}
                        helperText={errors.Duration?.message}
                        onChange={event => {
                          const newValue = event.target.value;
                          if (newValue === '' || parseInt(newValue, 10) > 0) {
                            field.onChange(
                              newValue === '' ? null : parseInt(newValue, 10)
                            );
                          }
                        }}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                </Stack>
                <Stack spacing={2} direction="column">
                  <StyledLabel>Description</StyledLabel>
                  <Controller
                    name="description"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        size="small"
                        InputProps={{
                          sx: {
                            typography: 'body2',
                            minHeight: '12vh',
                            alignItems: 'baseline'
                          }
                        }}
                        sx={{minHeight: '12vh'}}
                        error={!!errors.description}
                        helperText={errors.description?.message}
                      />
                    )}
                  />
                </Stack>
                <Stack direction="row">
                  <RHFCheckbox
                    name="MarkMandatory"
                    label="Mandatory Assignment"
                  />
                </Stack>

                <Stack display="flex" flexDirection="row-reverse">
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="info"
                    loading={isloaded}
                    disabled={isSubmitting}
                    onClick={() => {
                      if (!errors) {
                        onSubmit;
                      }
                    }}>
                    Save
                  </LoadingButton>
                </Stack>
              </Stack>
            </form>
          ))}
        {activeTab === 1 && (
          <NewFilesLockProperties
            uiFields={uiFields}
            insertdata={insertedId}
            tableNameEntity="ModuleOfflineTestSubList"
          />
        )}
        {activeTab === 2 && (
          <Resources
            uiFields={uiFields}
            insertdata={insertedId}
            tableNameEntity="ContentModuleResourseOfflineTest"
          />
        )}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="warning"
            sx={{width: '40vw'}}>
            Basic information data must be added before accessing other tabs.
          </Alert>
        </Snackbar>
        <Snackbar
          open={saved}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{width: '40vw'}}>
            Basic Info is Saved
          </Alert>
        </Snackbar>
      </Drawer>
    </FormProvider>
  );
}
